import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import { FadeIn } from '../../../../animations/FadeIn';

import { useDispatch } from 'react-redux';
import {
  registrationApi,
  useCreateRegistrationMutation,
  useGetRegistrationQuery,
  useUpdateRegistrationMutation
} from '../../../../../services/endpoints/content/registrations';
import { AppDispatch } from '../../../../../services/store';
import { RegistrationsForm } from './RegistrationsForm';

const steps = [{ name: 'Details', form: RegistrationsForm }];

export interface FormValues {
  id?: number;
  course_id?: { value?: number; label: string };
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;
export type CreateFormValues = Overwrite<
  FormValues,
  {
    course_id?: number;
  }
>;

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

const registrationLinksMap = (id?: number | string) => {
  return {
    edit: `/admin/registrations/${id}/edit`,
    detail: `/admin/registrations/${id}`,
    list: `/admin/registrations`
  };
};

interface AddEditRegistrationsProps {
  editing?: boolean;
}

export const AddEditRegistrations: React.FC<AddEditRegistrationsProps> = ({ editing }) => {
  const params = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [createRegistrations] = useCreateRegistrationMutation();
  const [updateRegistrations] = useUpdateRegistrationMutation();

  const {
    data: registrationData,
    error: registrationError,
    isLoading: registrationLoading
  } = useGetRegistrationQuery(Number(params.id!), { skip: !editing || !params.id });

  const [initialValues, setInitialValues] = useState<FormValues>({
    course_id: { label: '' }
  });

  const validationSchemas = [
    Yup.object({
      course_id: Yup.object({
        value: Yup.number().min(1, 'Course selection is required').required('Course selection is required'),
        label: Yup.string().optional()
      }).required()
    })
  ];

  useEffect(() => {
    if (editing && registrationError && !registrationLoading) {
      addToast('Error while loading admin', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, registrationError, registrationLoading]);

  useEffect(() => {
    let values;
    if (registrationData && registrationData?.registration) {
      values = registrationData.registration;
    }

    if (values?.id)
      setInitialValues({
        id: values.id,
        course_id: { value: values.course_id ?? undefined, label: values.title ?? '' }
      });
  }, [registrationData]);

  const formatFormValue = (formValue?: FormValues): CreateFormValues => {
    return JSON.parse(JSON.stringify(formValue)) as CreateFormValues;
  };

  const handleAddRegistrations = async (formValue: FormValues) => {
    try {
      let submission = formatFormValue(formValue);
      submission.course_id = formValue.course_id ? (formValue.course_id.value ?? 0) : undefined;

      if (editing) {
        const res = await updateRegistrations({
          ...submission,
          id: Number(params.id)!
        }).unwrap();
        if (res && res.registration?.id) {
          await dispatch(
            registrationApi.endpoints.getRegistration.initiate(res.registration.id, {
              forceRefetch: true,
              subscribe: false
            })
          );
          addToast(`Registration updated succesfully`, 'success');
          navigate('/admin/registrations/' + res.registration.id, { replace: true });
        }
      } else {
        const res = await createRegistrations(submission).unwrap();
        if (res && res.registration) {
          // navigate(registrationLinksMap(res.registration.id!)?.list);
          addToast('Registration created succesfully', 'success');
          navigate('/admin/registrations/' + res.registration.id, { replace: true });
          // navigate(-1 as To)
        } else throw new Error('A problem happened while creating Registration');
      }
    } catch (e) {
      if (e.data && e.data.error) {
        addToast(e.data.error[0], 'error');
      }
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        id: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text="Back"
            color="#2B4E64"
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Registration
          </h2>
          {steps.length > 1 && (
            <Stepper
              editing
              steps={steps}
              currentStep={currentStep}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleAddRegistrations}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
